import type { Practitioner } from '@/mocks/practitioners';
import { SITE_NAME, getSiteUrl, SITE_IMAGE_URL } from '@/lib/seoConfig';

export interface SchemaNode {
  [key: string]: unknown;
}

// Organisme éditeur du site (mise en relation patients / praticiens à Rabat).
export function buildOrganizationSchema(): SchemaNode {
  const siteUrl = getSiteUrl();
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    url: `${siteUrl}/`,
    logo: SITE_IMAGE_URL,
    description:
      "Plateforme marocaine de mise en relation entre patients et praticiens de santé à Rabat : médecins, laboratoires, radiologie et pharmacies.",
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Rabat',
      addressCountry: 'MA',
    },
    areaServed: {
      '@type': 'City',
      name: 'Rabat',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer support',
      email: 'contact@medicaclic.com',
      availableLanguage: ['fr'],
    },
  };
}

// Site web + action de recherche interne (utilisée par le champ de recherche).
export function buildWebSiteSchema(): SchemaNode {
  const siteUrl = getSiteUrl();
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: `${siteUrl}/`,
    inLanguage: 'fr',
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${siteUrl}/recherche?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };
}

// Page d'information générique (mentions légales, CGU, confidentialité).
export function buildWebPageSchema(name: string, description: string, path: string): SchemaNode {
  const siteUrl = getSiteUrl();
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name,
    description,
    url: `${siteUrl}${path}`,
    inLanguage: 'fr',
    isPartOf: {
      '@type': 'WebSite',
      name: SITE_NAME,
      url: `${siteUrl}/`,
    },
  };
}

// Fiche praticien : construit à partir des données réellement affichées.
export function buildPhysicianSchema(p: Practitioner): SchemaNode {
  const siteUrl = getSiteUrl();
  const node: SchemaNode = {
    '@context': 'https://schema.org',
    '@type': p.category === 'Pharmacies' ? 'Pharmacy' : 'Physician',
    name: p.name,
    url: `${siteUrl}/praticien/${p.id}`,
    address: {
      '@type': 'PostalAddress',
      streetAddress: p.address || undefined,
      addressLocality: 'Rabat',
      addressCountry: 'MA',
    },
    areaServed: {
      '@type': 'City',
      name: 'Rabat',
    },
  };

  if (p.type) node.medicalSpecialty = p.type;
  if (p.phone) node.telephone = p.phone;
  if (p.price > 0) node.priceRange = `${p.price} MAD`;

  if (typeof p.rating === 'number' && typeof p.reviews === 'number' && p.reviews > 0) {
    node.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: p.rating,
      reviewCount: p.reviews,
    };
  }

  return node;
}